"use client";
import * as S from "./styles.ts";
import BrandImage, { IBrandImageProps } from "./BrandImage";
import LinkCategories from "./Links";
import Links from "./Links";
import { TCategory } from "./Links/Category";
import useBreakpoint from "@/lib/helpers/hooks/useBreakpoint.ts";
import { useEffect, useState } from "react";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { getUser } from "@/redux/slices/user/thunks/getUser.ts";
import NavAuth from "./NavAuth";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { IImageProps } from "@/lib/types/components";

export interface INavigationSectionComponentProps {
  branding?: IImageProps;
  available_trial?: boolean;
  categories?: TCategory[];
  altStyling?: boolean;
}

const DesktopNav = ({
                      branding,
                      available_trial,
                      categories,
                      altStyling,
                    }: INavigationSectionComponentProps) => (
  <S.NavigationSectionContainer>
    <S.NavigationLeftWrapper>
      {branding && (
        <BrandImage
          src={branding.src}
          alt={branding.alt}
        />
      )}
      {categories && (
        <LinkCategories
          altStyling={altStyling}
          categories={categories}
          availableTrial={available_trial}
        />
      )}
    </S.NavigationLeftWrapper>
    <NavAuth
      available_trial={available_trial}
      altStyling={altStyling}
    />
  </S.NavigationSectionContainer>
);

const ToggleMobileNavButton = ({ isOpen, onClick }) => (
  <S.ToggleMobileNavButtonContainer
    $isOpen={isOpen}
    onClick={onClick}
    title="Open Navigation Menu"
  >
    <S.ToggleMobileNavButtonBar />
    <S.ToggleMobileNavButtonBar />
    <S.ToggleMobileNavButtonBar />
  </S.ToggleMobileNavButtonContainer>
);

const MobileNav = ({
                     branding,
                     available_trial,
                     categories,
                     altStyling,
                   }: INavigationSectionComponentProps) => {
  const path = usePathname();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [path]);

  return (
    <S.MobNavigationSectionContainer>
      <S.MobTopSection>
        <ToggleMobileNavButton
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        />

        {branding && (
          <BrandImage
            src={branding.src}
            alt={branding.alt}
          />
        )}
        <NavAuth
          available_trial={available_trial}
          altStyling={altStyling}
        />
      </S.MobTopSection>
      <AnimatePresence>
        {isOpen && (
          <S.MobileDropdownWrapper
            as={motion.div}
            initial={{
              height: 0,
              opacity: 0,
            }}
            animate={{
              height: "calc(100% - 61px)",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
          >
            <S.MobileDropdownContainer>
              <Links categories={categories} />
            </S.MobileDropdownContainer>
          </S.MobileDropdownWrapper>
        )}
      </AnimatePresence>
    </S.MobNavigationSectionContainer>
  );
};

const NavigationSectionComponent = ({
                                      branding,
                                      available_trial,
                                      categories,
                                      altStyling,
                                    }: INavigationSectionComponentProps) => {
  const dispatch = useTypedDispatch();
  const user = useTypedSelector((state) => state.user.user);
  const { check } = useBreakpoint("md");

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
      if (user.authenticated) return;
      dispatch(getUser());
    }
  }, [mounted, user]);

  return (
    <S.NavigationSectionWrapper $altStyling={altStyling}>
      {mounted && (
        <>
          {check ? (
            <DesktopNav
              branding={branding}
              available_trial={available_trial}
              categories={categories}
              altStyling={altStyling}
            />
          ) : (
            <MobileNav
              branding={branding}
              available_trial={available_trial}
              categories={categories}
              altStyling={altStyling}
            />
          )}
        </>
      )}
    </S.NavigationSectionWrapper>
  );
};

export default NavigationSectionComponent;
export const options = [
  {
    description: "",
    displayName: "NavigationSectionComponent",
    methods: [],
    props: {
      branding: {
        required: false,
        flowType: { name: "IBrandImageProps" },
        description: "",
      },
      available_trial: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
      categories: {
        required: false,
        flowType: {
          name: "Array",
          elements: [{ name: "TCategory" }],
          raw: "TCategory[]",
        },
        description: "",
      },
      altStyling: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
    },
  },
];
